import { inject } from '@angular/core';
import {
  type CanActivateChildFn,
  type CanActivateFn,
  type RedirectFunction,
  Router,
  type Routes,
  type UrlMatcher,
} from '@angular/router';
import {
  schoolbellCallbackRoute,
  spinoffAuthGuard,
} from '@sbe-spinoff-client/spinoff-platform';
import { REQUIRE_LOGIN } from './core/auth/require-login.token';
import { ConductActiveOrgService } from './core/org/conduct-active-org.service';
import { ConductMySeatsService } from './core/org/conduct-my-seats.service';

// Route data the app shell reads: sign-in pages and pages marked bare render
// without the shell, and some pages draw their own full-screen layout on mobile.
const AUTH_PAGE = { authPage: true };
const BARE_PAGE = { bare: true };
const MOBILE_BARE = { mobileBare: true };

// School pages had no school in their address before; /dashboard still has none (the default
// after sign-in, the connect page, a 「진입」 with no school). Bookmarks keep working too.
const SCHOOL_PAGES = new Set([
  'dashboard',
  'entries',
  'members',
  'settings',
  'me',
]);

const schoolPageWithoutSchool: UrlMatcher = (segments) => {
  if (segments.length === 0 || !SCHOOL_PAGES.has(segments[0].path)) {
    return null;
  }

  return { consumed: segments };
};

// The same page in the school this browser entered last, or how to enter one.
const openLastSchool: RedirectFunction = ({ url, queryParams }) => {
  const orgId = inject(ConductActiveOrgService).orgId();
  const router = inject(Router);

  if (!orgId) return router.createUrlTree(['/connect']);

  const page = url.map((segment) => segment.path);

  return router.createUrlTree(['/orgs', orgId, ...page], { queryParams });
};

const schoolbellCallback = schoolbellCallbackRoute({
  returnPath: '/dashboard',
  loginPath: '/login',
});

// The school in the URL is the one on screen; it is also remembered for `openLastSchool`.
// canActivateChild reruns on every page change, so moving to another school's URL switches.
const selectSchoolFromUrl: CanActivateChildFn = (route) => {
  const orgId = route.paramMap.get('orgId');

  if (orgId) inject(ConductActiveOrgService).select(orgId);

  return true;
};

// Staff pages call staff-only APIs; a student or guardian goes to their own page instead.
// If the seats cannot be read, let the page open and the APIs decide, as before.
export const conductFacultyGuard: CanActivateFn = async (route) => {
  const orgId = route.paramMap.get('orgId');
  const seats = inject(ConductMySeatsService);
  const router = inject(Router);

  if (!orgId) return true;
  if (!(await seats.ensure(orgId))) return true;
  if (seats.isFaculty()) return true;

  return router.createUrlTree(['/orgs', orgId, 'me']);
};

// Once a school is known, the connect page has nothing to say.
const noSchoolGuard: CanActivateFn = () => {
  if (!inject(ConductActiveOrgService).orgId()) return true;

  return inject(Router).createUrlTree(['/dashboard']);
};

const conductLoginGuard: CanActivateFn = (route, state) => {
  if (!inject(REQUIRE_LOGIN)) return true;

  return spinoffAuthGuard(route, state);
};

export const appRoutes: Routes = [
  // Public entry: the login page itself, never an automatic redirect.
  {
    path: '',
    pathMatch: 'full',
    data: AUTH_PAGE,
    title: $localize`:@@conductLoginRouteTitle:로그인`,
    loadComponent: () =>
      import('./features/auth/auth-login/auth-login.component').then(
        (m) => m.AuthLoginComponent,
      ),
  },
  {
    path: 'login',
    data: AUTH_PAGE,
    title: $localize`:@@conductLoginRouteTitle:로그인`,
    loadComponent: () =>
      import('./features/auth/auth-login/auth-login.component').then(
        (m) => m.AuthLoginComponent,
      ),
  },
  {
    // OIDC redirect_uri registered for this app (USER_OAUTH_REDIRECT_URI).
    ...schoolbellCallback,
    data: { ...schoolbellCallback.data, ...AUTH_PAGE },
    title: $localize`:@@conductLoginRouteTitle:로그인`,
  },
  {
    // 학교종이 관리자 화면의 「진입」(magic link). Signs in with the token in the URL, then
    // goes to /orgs/:orgId (or /dashboard). Shared with the timetable and sms web apps.
    path: 'auth/erps/:erpId/callback',
    data: AUTH_PAGE,
    title: $localize`:@@conductLoginRouteTitle:로그인`,
    loadComponent: () =>
      import('@sbe-spinoff-client/schoolbell-org-link').then(
        (m) => m.ErpCallbackComponent,
      ),
  },
  { matcher: schoolPageWithoutSchool, redirectTo: openLastSchool },

  // With REQUIRE_LOGIN, every conduct page requires a session and the guard redirects
  // to /login?return=<url>. canActivate alone does not rerun between child pages,
  // so canActivateChild checks the session on every page change.
  {
    path: '',
    canActivate: [conductLoginGuard],
    canActivateChild: [conductLoginGuard],
    children: [
      {
        path: 'connect',
        // Shown alone, like the sign-in page: the menus need a school.
        data: BARE_PAGE,
        canActivate: [noSchoolGuard],
        title: $localize`:@@conductConnectRouteTitle:학교 연결`,
        loadComponent: () =>
          import('./features/school-connect/school-connect.component').then(
            (m) => m.SchoolConnectComponent,
          ),
      },
    ],
  },

  // A school's pages (conventions-api-web-paths.md §1-0·1-1). Same session check as above.
  {
    path: 'orgs/:orgId',
    children: [
      {
        path: '',
        canActivate: [conductLoginGuard],
        canActivateChild: [conductLoginGuard, selectSchoolFromUrl],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
          // A team 「진입」 opens the school's home; conduct has no team pages.
          { path: 'teams/:teamId', redirectTo: 'dashboard' },
          {
            path: 'me',
            title: $localize`:@@conductMeRouteTitle:내 상벌점`,
            loadComponent: () =>
              import('./features/me/my-conduct/my-conduct.component').then(
                (m) => m.MyConductComponent,
              ),
          },
          {
            // 학교 구성원 목록·멤버 추가 — 앱 간 공유 구성원 화면(spinoff-platform lib)을 끼워 쓴다.
            path: 'members',
            canActivate: [conductFacultyGuard],
            title: $localize`:@@conductMembersRouteTitle:구성원`,
            loadComponent: () =>
              import('./features/org-members/org-members.component').then(
                (m) => m.ConductOrgMembersComponent,
              ),
          },
          {
            path: 'settings',
            canActivate: [conductFacultyGuard],
            title: $localize`:@@conductSettingsRouteTitle:설정`,
            loadComponent: () =>
              import(
                './features/settings/settings-layout/settings-layout.component'
              ).then((m) => m.SettingsLayoutComponent),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'items' },
              {
                path: 'items',
                loadComponent: () =>
                  import(
                    './features/items/item-settings/item-settings.component'
                  ).then((m) => m.ItemSettingsComponent),
              },
              {
                path: 'thresholds',
                loadComponent: () =>
                  import(
                    './features/thresholds/threshold-settings/threshold-settings.component'
                  ).then((m) => m.ThresholdSettingsComponent),
              },
              {
                path: 'score-reset',
                loadComponent: () =>
                  import(
                    './features/score-reset/score-reset-settings/score-reset-settings.component'
                  ).then((m) => m.ScoreResetSettingsComponent),
              },
              {
                path: 'notifications',
                loadComponent: () =>
                  import(
                    './features/notifications/notification-settings/notification-settings.component'
                  ).then((m) => m.NotificationSettingsComponent),
              },
            ],
          },

          { path: 'entries', pathMatch: 'full', redirectTo: 'entries/history' },
          {
            path: 'entries/history/:memberId',
            canActivate: [conductFacultyGuard],
            data: MOBILE_BARE,
            title: $localize`:@@conductMemberRouteTitle:학생 상세`,
            loadComponent: () =>
              import(
                './features/members/member-detail/member-detail.component'
              ).then((m) => m.MemberDetailComponent),
          },
          {
            path: 'entries/grant',
            canActivate: [conductFacultyGuard],
            data: MOBILE_BARE,
            title: $localize`:@@conductGrantRouteTitle:상벌점 부여`,
            loadComponent: () =>
              import(
                './features/entries/entry-grant/entry-grant.component'
              ).then((m) => m.EntryGrantComponent),
          },
          {
            path: 'entries/history',
            canActivate: [conductFacultyGuard],
            data: MOBILE_BARE,
            title: $localize`:@@conductHistoryTitle:상벌점 조회`,
            loadComponent: () =>
              import(
                './features/entries/entry-history/entry-history.component'
              ).then((m) => m.EntryHistoryComponent),
          },
          {
            path: 'dashboard',
            canActivate: [conductFacultyGuard],
            data: MOBILE_BARE,
            title: $localize`:@@conductHomeRouteTitle:홈`,
            loadComponent: () =>
              import('./features/home/home.component').then(
                (m) => m.HomeComponent,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
